import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { FileBase64, FileUploadComponent } from "@components/file-upload/file-upload.component";
import { InfoComponent } from "@components/info/info.component";
import { InputComponent } from "@components/input/input.component";
import { Observable, Subject, forkJoin, of } from "rxjs";
import {
    map,
    shareReplay,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";

import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ResendApteringspaketRequestDTO } from "~app/common/models/arbetsorder/ResendApteringspaketRequestDTO";
import { Maskinfil } from "~app/common/models/arbetsorder/maskinlag/BifogaMaskinfilerRequestDto";
import { MaskinInformationDTO } from "~app/common/models/arbetsorder/maskinlag/MaskinInformationDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SandningsloggTexts } from "../sandningslogg.texts";
import { SandningsloggSearchService } from "../search-panel/sandningslogg-search.service";

@Component({
    selector: "rask-search-result-maskin",
    templateUrl: "./search-result-maskin.component.html",
    styleUrls: ["./search-result-maskin.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputComponent,
        CheckboxComponent,
        FormsModule,
        ButtonComponent,
        InfoComponent,
        FileUploadComponent,
        AsyncPipe
    ]
})
export class SearchResultMaskinComponent implements OnDestroy, OnInit {
    maskinInfo: MaskinInformationDTO;
    texts = SandningsloggTexts.texts;

    protected manualFileUploadDisabled$: Observable<boolean>;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly modalDialogService = inject(RaskModalService);
    private readonly sandningsloggService = inject(SandningsloggSearchService);
    private readonly toastService = inject(ToastService);
    private readonly unsubscribe$ = new Subject<void>();

    constructor() {
        this.sandningsloggService.maskin$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(x => {
                this.maskinInfo = x;
            });
    }
    ngOnInit(): void {
        this.manualFileUploadDisabled$ = this.sandningsloggService.maskin$.pipe(
            map(m => !(m.filskickaAktivt && m.senderApteringsfiler && this.canEdit())),
            shareReplay(1)
        );
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    canSend(): boolean {
        return this.maskinInfo.kanSkickasOm && this.canEdit() && this.maskinInfo.aktiv;
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    onFileChange(files: FileBase64[]) {
        const file$ = files.map(this.mapFile);

        const work$ = forkJoin(file$).pipe(
            switchMap(f => this.sandningsloggService.bifogaMaskinfiler({
                files: f,
                maskinnummer: this.maskinInfo.maskinnummer
            })),
            tap(res => {
                this.toastService.add(`${res.fileCount} filer kommer att skickas`);
            })
        );

        const worker = this.busyService.addWork(work$);
        worker.subscribe();
    }

    rensaLogg(): void {
        const worker = this.busyService.addWork(this.sandningsloggService.rensaLogg(this.maskinInfo.maskinnummer));
        worker.subscribe(result => {
            this.toastService.add(`${result} ${this.texts.tabortLoggConfirmText} ${this.maskinInfo.maskinnummer}`);
        });
    }

    skickaAktuellaOIN(): void {
        this.skickaFiler(GuidUtils.newGuid(), null);
    }

    skickaFiler(resendArbetsorderCorrelationGuid?: string, resendDependenciesCorrelationGuid?: string) {
        const request = new ResendApteringspaketRequestDTO();
        request.maskinId = this.maskinInfo.maskinId;
        request.maskinTyp = this.maskinInfo.maskintyp;
        request.maskinnummer = this.maskinInfo.maskinnummer;
        request.resendArbetsorderCorrelationGuid = resendArbetsorderCorrelationGuid;
        request.resendDependenciesCorrelationGuid = resendDependenciesCorrelationGuid;

        const worker = this.busyService.addWork(this.sandningsloggService.skickaOmFiler(request));
        worker.subscribe(result => {
            this.toastService.add(`Apteringsfiler för ${result.antalSkapadeApteringspaket} arbetsorder kommer att skickas.`);
        });
    }

    skickaOmFiler(): void {
        const batchGuid = GuidUtils.newGuid();
        if (ObjectUtils.isNullOrUndefined(this.maskinInfo.gpxnummer)) {
            this.modalDialogService.info(new ModalSettings("GPX nummer saknas", this.texts.gpxnrsaknas));
        } else {
            this.skickaFiler(batchGuid, batchGuid);
        }
    }

    private readonly mapFile = (file: FileBase64): Observable<Maskinfil> => of({
        contentBase64: file.content,
        fileName: file.name
    });
}
